import {
  BarSeries,
  Category,
  Chart,
  ColumnSeries,
  DataLabel,
  FontModel,
  IAxisLabelClickEventArgs,
  IAxisLabelRenderEventArgs,
  ILegendClickEventArgs,
  ILegendRenderEventArgs,
  IPointEventArgs,
  ITextRenderEventArgs,
  Legend,
  SeriesModel,
  StackingBarSeries,
  StackingColumnSeries,
  Tooltip,
} from '@syncfusion/ej2-charts';
import { DataManager, UrlAdaptor } from '@syncfusion/ej2-data';
import { ComboBox } from '@syncfusion/ej2-dropdowns';

import {
  CompanyDeadlineMonthlyView,
  CompanyDeadlineView,
  ServiceDeadlineMonthlyView,
  ServiceStatsView,
} from '@prisma/client';
import Cookies from 'js-cookie';

import { FONT_FAMILY } from '@/shared/constants';
import { COOKIES } from '@/shared/cookies';
import { routes } from '@/shared/routes';
import { DashboardView, DueTimeFrame, DueTimeFrameType, TaskStatus } from '@/shared/types/enum';
import { enumOptions } from '@/shared/utils/enum';
import { formatEnumKey, formatLongMonth } from '@/shared/utils/format';
import { companiesFilterUrl, tasksFilterUrl } from '@/shared/utils/url';
import { utcAddMonths, utcToday } from '@/shared/utils/utc';

Chart.Inject(ColumnSeries, StackingColumnSeries, BarSeries, StackingBarSeries, Category, DataLabel, Legend, Tooltip);

document.addEventListener('DOMContentLoaded', () => {
  if (document.getElementById('dashboard')) {
    initViewPicker();
    initUserPicker();

    const view = Cookies.get(COOKIES.DASHBOARD_VIEW.name) ?? Cookies.get(COOKIES.DEFAULT_DASHBOARD_VIEW.name);

    if (view === DashboardView.CompanyDeadlines) {
      initCompanyDeadlineChart();
      initCompanyDeadlineMonthlyChart();
    } else if (view === DashboardView.TaskDeadlines) {
      initTaskDeadlineChart();
      initTaskDeadlineMonthlyChart();
    }
  }
});

const initViewPicker = () => {
  const input = document.getElementById('view-picker') as HTMLInputElement;

  const viewPicker = new ComboBox({
    dataSource: enumOptions(DashboardView),
    fields: {
      value: 'value',
      text: 'text',
    },
    value: Cookies.get(COOKIES.DASHBOARD_VIEW.name) ?? Cookies.get(COOKIES.DEFAULT_DASHBOARD_VIEW.name),
    allowCustom: false,
    showClearButton: false,
    change: () => {
      const view = viewPicker.value as string;

      Cookies.set(COOKIES.DASHBOARD_VIEW.name, view);

      location.reload();
    },
  });

  viewPicker.appendTo(input);
};

const initUserPicker = () => {
  const input = document.getElementById('user-picker') as HTMLInputElement;

  const userPicker = new ComboBox({
    dataSource: new DataManager({
      url: routes.data.lookup.list({ model: 'MemberView' }),
      adaptor: new UrlAdaptor(),
    }),
    fields: {
      value: 'value',
      text: 'text',
    },
    placeholder: 'All Users',
    sortOrder: 'Ascending',
    value: Cookies.get(COOKIES.USER_ID_FILTER.name),
    allowCustom: false,
    change: () => {
      const userId = userPicker.value as string;

      if (userId) {
        Cookies.set(COOKIES.USER_ID_FILTER.name, userId);
      } else {
        Cookies.remove(COOKIES.USER_ID_FILTER.name);
      }

      location.reload();
    },
  });

  userPicker.appendTo(input);
};

const initCompanyDeadlineChart = () => {
  const element = document.getElementById('deadline-chart') as HTMLDivElement;

  const companyDeadlineViews: CompanyDeadlineView[] = JSON.parse(element.dataset.data!);

  if (companyDeadlineViews.length > 0) {
    const font: FontModel = {
      fontFamily: FONT_FAMILY,
      size: '0.875rem',
    };

    type CompanyDeadlineViewSeries = SeriesModel & {
      xName: keyof CompanyDeadlineView;
      yName: keyof CompanyDeadlineView;
    };

    const companyDeadlineViewSeries = (
      props: Pick<CompanyDeadlineViewSeries, 'name' | 'xName' | 'yName'>,
    ): CompanyDeadlineViewSeries => {
      return {
        ...props,
        type: 'StackingBar100',
        dataSource: companyDeadlineViews,
        marker: {
          dataLabel: {
            visible: true,
            format: 'n',
            position: 'Middle',
            font: {
              ...font,
              color: '#FFF',
            },
          },
        },
        border: {
          width: 0,
        },
        animation: {
          duration: 300,
        },
      };
    };

    const series: CompanyDeadlineViewSeries[] = [
      companyDeadlineViewSeries({
        name: 'Due to start',
        xName: 'type',
        yName: 'due30To60Days',
      }),
      companyDeadlineViewSeries({
        name: 'Due to complete',
        xName: 'type',
        yName: 'due30Days',
      }),
      companyDeadlineViewSeries({
        name: 'Completed (30 days)',
        xName: 'type',
        yName: 'due',
      }),
    ];

    const chart: Chart = new Chart({
      titleStyle: font,
      chartArea: {
        border: {
          width: 0,
        },
      },
      series,
      primaryXAxis: {
        valueType: 'Category',
        majorGridLines: { width: 0 },
        majorTickLines: { width: 0 },
        titleStyle: font,
        labelStyle: font,
      },
      primaryYAxis: {
        edgeLabelPlacement: 'Shift',
        majorTickLines: { width: 0 },
        lineStyle: { width: 0 },
        titleStyle: font,
        labelStyle: font,
        visible: false,
      },
      palettes: ['#01CBCD', '#FF8C00', '#DC3545'],
      legendSettings: {
        visible: false,
      },
      height: `${companyDeadlineViews.length * 50}px`,
      axisLabelRender: (args: IAxisLabelRenderEventArgs) => {
        args.text = formatEnumKey(args.text);
      },
      textRender: (args: ITextRenderEventArgs) => {
        args.cancel = args.point.y === 0;
      },
      pointClick: (args: IPointEventArgs) => {
        const dueTimeFrameType = args.point.x as DueTimeFrameType;

        const { yName: dueTimeFrame } = args.series;

        if (dueTimeFrame === 'due30To60Days') {
          location.href = companiesFilterUrl({
            dueTimeFrame: DueTimeFrame.DueWithin30To60Days,
            dueTimeFrameType,
          });
        } else if (dueTimeFrame === 'due30Days') {
          location.href = companiesFilterUrl({
            dueTimeFrame: DueTimeFrame.DueWithin30Days,
            dueTimeFrameType,
          });
        } else if (dueTimeFrame === 'due') {
          location.href = companiesFilterUrl({
            dueTimeFrame: DueTimeFrame.Due,
            dueTimeFrameType,
          });
        }
      },
    });

    chart.appendTo(element);
  } else {
    const placeholder = element.querySelector('.chart-placeholder') as HTMLDivElement;

    placeholder.classList.remove('d-none');
    placeholder.classList.add('d-block');
  }
};

const initCompanyDeadlineMonthlyChart = () => {
  const element = document.getElementById('monthly-deadline-chart') as HTMLDivElement;

  const items: CompanyDeadlineMonthlyView[] = JSON.parse(element.dataset.data!);

  if (items.length > 0) {
    const font: FontModel = {
      fontFamily: FONT_FAMILY,
      size: '0.875rem',
    };

    type Series = SeriesModel & { xName: keyof CompanyDeadlineMonthlyView; yName: keyof CompanyDeadlineMonthlyView };

    const serviceViewSeries = (props: Pick<Series, 'name' | 'xName' | 'yName' | 'groupName'>): Series => {
      return {
        ...props,
        type: 'Column',
        dataSource: items,
        marker: {
          dataLabel: {
            visible: true,
            format: 'n',
            position: 'Middle',
            font: {
              ...font,
              color: '#FFF',
            },
          },
        },
        border: {
          width: 0,
        },
        animation: {
          duration: 300,
        },
        enableTooltip: true,
      };
    };

    const series: Series[] = [
      serviceViewSeries({
        name: formatLongMonth(utcToday()),
        xName: 'type',
        yName: 'month1DueCount',
        groupName: 'month1',
      }),
      serviceViewSeries({
        name: formatLongMonth(utcAddMonths(utcToday(), 1)),
        xName: 'type',
        yName: 'month2DueCount',
        groupName: 'month2',
      }),
      serviceViewSeries({
        name: formatLongMonth(utcAddMonths(utcToday(), 2)),
        xName: 'type',
        yName: 'month3DueCount',
        groupName: 'month3',
      }),
    ];

    const chart: Chart = new Chart({
      titleStyle: font,
      chartArea: {
        border: {
          width: 0,
        },
      },
      series,
      primaryXAxis: {
        valueType: 'Category',
        majorGridLines: { width: 1 },
        majorTickLines: { width: 1 },
        titleStyle: font,
        labelStyle: font,
      },
      primaryYAxis: {
        majorTickLines: { width: 0 },
        lineStyle: { width: 0 },
        titleStyle: font,
        labelStyle: font,
        visible: false,
      },
      palettes: ['#01CBCD', '#0A415C', '#5C636A'],
      legendSettings: {
        textStyle: font,
        padding: 20,
        toggleVisibility: false,
      },
      legendClick: (args: ILegendClickEventArgs) => {
        const { yName: countName } = args.series as Series;

        if (countName === 'month1DueCount') {
          location.href = companiesFilterUrl({
            dueTimeFrame: DueTimeFrame.DueThisMonth,
          });
        } else if (countName === 'month2DueCount') {
          location.href = companiesFilterUrl({
            dueTimeFrame: DueTimeFrame.DueNextMonth,
          });
        } else if (countName === 'month3DueCount') {
          location.href = companiesFilterUrl({
            dueTimeFrame: DueTimeFrame.DueMonthAfterNext,
          });
        }
      },
      height: '500px',
      axisLabelRender: (args: IAxisLabelRenderEventArgs) => {
        args.text = formatEnumKey(args.text);
      },
      textRender: (args: ITextRenderEventArgs) => {
        args.cancel = args.point.y === 0;
      },
      pointClick: (args: IPointEventArgs) => {
        const dueTimeFrameType = args.point.x as DueTimeFrameType;

        const { yName: countName } = args.series as Series;

        if (countName === 'month1DueCount') {
          location.href = companiesFilterUrl({
            dueTimeFrame: DueTimeFrame.DueThisMonth,
            dueTimeFrameType,
          });
        } else if (countName === 'month2DueCount') {
          location.href = companiesFilterUrl({
            dueTimeFrame: DueTimeFrame.DueNextMonth,
            dueTimeFrameType,
          });
        } else if (countName === 'month3DueCount') {
          location.href = companiesFilterUrl({
            dueTimeFrame: DueTimeFrame.DueMonthAfterNext,
            dueTimeFrameType,
          });
        }
      },
    });

    chart.appendTo(element);
  } else {
    const placeholder = element.querySelector('.chart-placeholder') as HTMLDivElement;

    placeholder.classList.remove('d-none');
    placeholder.classList.add('d-block');
  }
};

const initTaskDeadlineChart = () => {
  const element = document.getElementById('deadline-chart') as HTMLDivElement;

  const serviceViews: ServiceStatsView[] = JSON.parse(element.dataset.data!);

  if (serviceViews.length > 0) {
    const font: FontModel = {
      fontFamily: FONT_FAMILY,
      size: '0.875rem',
    };

    type ServiceStatsViewSeries = SeriesModel & { xName: keyof ServiceStatsView; yName: keyof ServiceStatsView };

    const serviceViewSeries = (
      props: Pick<ServiceStatsViewSeries, 'name' | 'xName' | 'yName'>,
    ): ServiceStatsViewSeries => {
      return {
        ...props,
        type: 'StackingBar100',
        dataSource: serviceViews,
        marker: {
          dataLabel: {
            visible: true,
            format: 'n',
            position: 'Middle',
            font: {
              ...font,
              color: '#FFF',
            },
          },
        },
        border: {
          width: 0,
        },
        animation: {
          duration: 300,
        },
      };
    };

    const series: ServiceStatsViewSeries[] = [
      serviceViewSeries({
        name: 'Due to start',
        xName: 'name',
        yName: 'startDueTaskCount',
      }),
      serviceViewSeries({
        name: 'Started',
        xName: 'name',
        yName: 'startedTaskCount',
      }),
      serviceViewSeries({
        name: 'Due to complete',
        xName: 'name',
        yName: 'completionDueTaskCount',
      }),
      serviceViewSeries({
        name: 'Overdue',
        xName: 'name',
        yName: 'deadlineDateDueTaskCount',
      }),
      serviceViewSeries({
        name: 'Completed (30 days)',
        xName: 'name',
        yName: 'completed30DaysTaskCount',
      }),
    ];

    const chart: Chart = new Chart({
      titleStyle: font,
      chartArea: {
        border: {
          width: 0,
        },
      },
      series,
      primaryXAxis: {
        valueType: 'Category',
        majorGridLines: { width: 0 },
        majorTickLines: { width: 0 },
        titleStyle: font,
        labelStyle: font,
      },
      primaryYAxis: {
        edgeLabelPlacement: 'Shift',
        majorTickLines: { width: 0 },
        lineStyle: { width: 0 },
        titleStyle: font,
        labelStyle: font,
        visible: false,
      },
      palettes: ['#01CBCD', '#FFC107', '#FF8C00', '#DC3545', '#198754'],
      legendSettings: {
        visible: false,
      },
      height: `${serviceViews.length * 50}px`,
      axisLabelRender: (args: IAxisLabelRenderEventArgs) => {
        if (args.text === '(No Service)') {
          args.labelStyle.color = '#adb5bd';
        }
      },
      textRender: (args: ITextRenderEventArgs) => {
        args.cancel = args.point.y === 0;
      },
      axisLabelClick: (args: IAxisLabelClickEventArgs) => {
        const { text: serviceName } = args;
        const { id: serviceId } = serviceViews.filter((serviceView) => serviceView.name === serviceName)[0];

        location.href = tasksFilterUrl({ serviceId, preset: 'uncompleted' });
      },
      pointClick: (args: IPointEventArgs) => {
        const { x: serviceName } = args.point;
        const { yName: countName } = args.series as ServiceStatsViewSeries;

        const { id: serviceId } = serviceViews.filter((serviceView) => serviceView.name === serviceName)[0];

        if (countName === 'startDueTaskCount') {
          location.href = tasksFilterUrl({
            serviceId,
            status: TaskStatus.StartDue,
          });
        } else if (countName === 'startedTaskCount') {
          location.href = tasksFilterUrl({
            serviceId,
            status: TaskStatus.Started,
          });
        } else if (countName === 'completionDueTaskCount') {
          location.href = tasksFilterUrl({
            serviceId,
            status: TaskStatus.CompletionDue,
          });
        } else if (countName === 'deadlineDateDueTaskCount') {
          location.href = tasksFilterUrl({
            serviceId,
            status: TaskStatus.DeadlineDue,
          });
        } else if (countName === 'completed30DaysTaskCount') {
          location.href = tasksFilterUrl({
            serviceId,
            preset: 'completed30Days',
          });
        }
      },
    });

    chart.appendTo(element);
  } else {
    const placeholder = element.querySelector('.chart-placeholder') as HTMLDivElement;

    placeholder.classList.remove('d-none');
    placeholder.classList.add('d-block');
  }
};

const initTaskDeadlineMonthlyChart = () => {
  const element = document.getElementById('monthly-deadline-chart') as HTMLDivElement;

  const items: ServiceDeadlineMonthlyView[] = JSON.parse(element.dataset.data!);

  if (items.length > 0) {
    const font: FontModel = {
      fontFamily: FONT_FAMILY,
      size: '0.875rem',
    };

    type Series = SeriesModel & { xName: keyof ServiceDeadlineMonthlyView; yName: keyof ServiceDeadlineMonthlyView };

    const serviceViewSeries = (
      props: Pick<Series, 'name' | 'xName' | 'yName' | 'groupName' | 'stackingGroup' | 'opacity'>,
    ): Series => {
      return {
        ...props,
        type: 'StackingColumn',
        dataSource: items,
        marker: {
          dataLabel: {
            visible: true,
            format: 'n',
            position: 'Middle',
            font: {
              ...font,
              color: '#FFF',
            },
          },
        },
        border: {
          width: 0,
        },
        animation: {
          duration: 300,
        },
        enableTooltip: true,
      };
    };

    const series: Series[] = [
      serviceViewSeries({
        name: formatLongMonth(utcToday()),
        xName: 'name',
        yName: 'month1DueTaskCount',
        groupName: 'month1',
        stackingGroup: 'month1',
      }),
      serviceViewSeries({
        name: formatLongMonth(utcAddMonths(utcToday(), 1)),
        xName: 'name',
        yName: 'month2DueTaskCount',
        groupName: 'month2',
        stackingGroup: 'month2',
      }),
      serviceViewSeries({
        name: formatLongMonth(utcAddMonths(utcToday(), 2)),
        xName: 'name',
        yName: 'month3DueTaskCount',
        groupName: 'month3',
        stackingGroup: 'month3',
      }),
      serviceViewSeries({
        name: `${formatLongMonth(utcToday())} (completed)`,
        xName: 'name',
        yName: 'month1CompletedTaskCount',
        groupName: 'month1',
        stackingGroup: 'month1',
        opacity: 0.2,
      }),
      serviceViewSeries({
        name: `${formatLongMonth(utcAddMonths(utcToday(), 1))} (completed)`,
        xName: 'name',
        yName: 'month2CompletedTaskCount',
        groupName: 'month2',
        stackingGroup: 'month2',
        opacity: 0.2,
      }),
      serviceViewSeries({
        name: `${formatLongMonth(utcAddMonths(utcToday(), 2))} (completed)`,
        xName: 'name',
        yName: 'month3CompletedTaskCount',
        groupName: 'month3',
        stackingGroup: 'month3',
        opacity: 0.2,
      }),
    ];

    const chart: Chart = new Chart({
      titleStyle: font,
      chartArea: {
        border: {
          width: 0,
        },
      },
      series,
      primaryXAxis: {
        valueType: 'Category',
        majorGridLines: { width: 1 },
        majorTickLines: { width: 1 },
        titleStyle: font,
        labelStyle: font,
      },
      primaryYAxis: {
        majorTickLines: { width: 0 },
        lineStyle: { width: 0 },
        titleStyle: font,
        labelStyle: font,
        visible: false,
      },
      palettes: ['#01CBCD', '#0A415C', '#5C636A'],
      legendSettings: {
        textStyle: font,
        padding: 20,
        toggleVisibility: false,
      },
      height: '500px',
      legendRender: (args: ILegendRenderEventArgs) => {
        if (args.text.endsWith('(completed)')) {
          args.cancel = true;
        }
      },
      legendClick: (args: ILegendClickEventArgs) => {
        const { yName: countName } = args.series as Series;

        if (countName === 'month1DueTaskCount') {
          location.href = tasksFilterUrl({
            dueTimeFrame: DueTimeFrame.DueThisMonth,
            preset: 'uncompleted',
          });
        } else if (countName === 'month2DueTaskCount') {
          location.href = tasksFilterUrl({
            dueTimeFrame: DueTimeFrame.DueNextMonth,
            preset: 'uncompleted',
          });
        } else if (countName === 'month3DueTaskCount') {
          location.href = tasksFilterUrl({
            dueTimeFrame: DueTimeFrame.DueMonthAfterNext,
            preset: 'uncompleted',
          });
        }
      },
      axisLabelRender: (args: IAxisLabelRenderEventArgs) => {
        if (args.text === '(No Service)') {
          args.labelStyle.color = '#adb5bd';
        }
      },
      textRender: (args: ITextRenderEventArgs) => {
        args.cancel = args.point.y === 0;
      },
      pointClick: (args: IPointEventArgs) => {
        const { x: serviceName } = args.point;
        const { yName: countName } = args.series as Series;
        const { id: serviceId } = items.filter((item) => item.name === serviceName)[0];

        if (countName === 'month1DueTaskCount') {
          location.href = tasksFilterUrl({
            serviceId,
            dueTimeFrame: DueTimeFrame.DueThisMonth,
            preset: 'uncompleted',
          });
        } else if (countName === 'month2DueTaskCount') {
          location.href = tasksFilterUrl({
            serviceId,
            dueTimeFrame: DueTimeFrame.DueNextMonth,
            preset: 'uncompleted',
          });
        } else if (countName === 'month3DueTaskCount') {
          location.href = tasksFilterUrl({
            serviceId,
            dueTimeFrame: DueTimeFrame.DueMonthAfterNext,
            preset: 'uncompleted',
          });
        } else if (countName === 'month1CompletedTaskCount') {
          location.href = tasksFilterUrl({
            serviceId,
            dueTimeFrame: DueTimeFrame.DueThisMonth,
            status: TaskStatus.Completed,
          });
        } else if (countName === 'month2CompletedTaskCount') {
          location.href = tasksFilterUrl({
            serviceId,
            dueTimeFrame: DueTimeFrame.DueNextMonth,
            status: TaskStatus.Completed,
          });
        } else if (countName === 'month3CompletedTaskCount') {
          location.href = tasksFilterUrl({
            serviceId,
            dueTimeFrame: DueTimeFrame.DueMonthAfterNext,
            status: TaskStatus.Completed,
          });
        }
      },
    });

    chart.appendTo(element);
  } else {
    const placeholder = element.querySelector('.chart-placeholder') as HTMLDivElement;

    placeholder.classList.remove('d-none');
    placeholder.classList.add('d-block');
  }
};
